import React from 'react'
import { HiExternalLink } from 'react-icons/hi'

const AppAdmin = () => {
  const tailwindColors = {
    gray: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    red: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    orange: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    amber: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    yellow: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    lime: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    green: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    emerald: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    teal: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    cyan: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    sky: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    blue: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    indigo: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    violet: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    purple: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    fuchsia: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    pink: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    rose: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    slate: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    zinc: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    neutral: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'],
    stone: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950']
  }

  const getColorClass = (color, shade) => {
    return `bg-${color}-${shade}`
  }

  const getTextColor = (shade) => {
    const shadeNum = parseInt(shade)
    if (shadeNum >= 500) return 'text-white'
    return 'text-gray-900'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Application Administration
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage application settings, themes, and system configuration.
            </p>
          </div>
          <a
            href="https://tailwindcss.com/docs/customizing-colors"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
          >
            Tailwind Colors Documentation
            <HiExternalLink className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Tailwind Color Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Tailwind CSS Color Palette
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Complete reference of all available Tailwind CSS colors as of current version
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {Object.entries(tailwindColors).map(([colorName, shades]) => (
              <div key={colorName} className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white capitalize">
                  {colorName}
                </h3>
                <div className="grid grid-cols-11 gap-1">
                  {shades.map((shade) => (
                    <div
                      key={shade}
                      className={`h-8 rounded flex items-center justify-center text-xs font-mono ${getColorClass(colorName, shade)} ${getTextColor(shade)}`}
                      title={`${colorName}-${shade}`}
                    >
                      {shade}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Theme Configuration
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Theme
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Color
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="blue">Blue</option>
                <option value="green">Green</option>
                <option value="purple">Purple</option>
                <option value="red">Red</option>
                <option value="indigo">Indigo</option>
              </select>
            </div>
            <button className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200">
              Save Theme Settings
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            System Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">App Version</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">React Version</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">18.2.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Tailwind CSS</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">3.4.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Build Date</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {new Date().toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* API Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          API Configuration
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Base URL
            </label>
            <input
              type="text"
              value="https://api.cityseecityfix.com"
              readOnly
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Documentation
            </label>
            <a
              href="https://api.cityseecityfix.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              View API Documentation
              <HiExternalLink className="ml-1 h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Maintenance Mode
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enable maintenance mode to restrict access during system updates
            </p>
          </div>
          <button className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg transition-colors duration-200">
            Enable Maintenance Mode
          </button>
        </div>
      </div>
    </div>
  )
}

export default AppAdmin
